import React from "react";
import { AlertTriangle } from "lucide-react";
import { useSiteContext } from "../contexts/SiteContext";
import { useMCPContext, type PrioritizedIssue } from "../hooks/useMCPContext";
import { type ReportRecommendations } from "../services/mcpService";
import DataPanel from "./DataPanel";
import EmptyState from "./EmptyState";
import WidgetExportBtn from "../reports/WidgetExportBtn";

interface ActionPlanIssuesPanelProps {
  cardId: string;
  title?: string;
  fallback?: React.ReactNode;
}

const priorityColor = (priority: PrioritizedIssue["priority"]) => {
  if (priority === "HIGH") return { bg: "rgba(248,113,113,0.15)", text: "#F87171", border: "rgba(248,113,113,0.3)", dot: "#F87171" };
  if (priority === "LOW") return { bg: "rgba(74,222,128,0.15)", text: "#4ADE80", border: "rgba(74,222,128,0.3)", dot: "#4ADE80" };
  return { bg: "rgba(255,154,74,0.15)", text: "#FF9A4A", border: "rgba(255,154,74,0.3)", dot: "#FF9A4A" };
};

const ActionPlanIssueRow: React.FC<{ issue: PrioritizedIssue }> = ({ issue }) => {
  if (issue.status === "fixed") {
    return (
      <div
        className="flex items-start justify-between gap-3 rounded-xl px-4 py-3"
        style={{
          background: "rgba(34, 197, 94, 0.1)",
          border: "1px solid rgba(34, 197, 94, 0.25)",
          borderLeft: "3px solid #22C55E",
          opacity: 0.85,
        }}
      >
        <div className="flex items-start gap-3 flex-1">
          <span className="text-sm mt-0.5">✅</span>
          <div className="text-sm font-medium line-through" style={{ color: "#9AA0B3" }}>
            {issue.title}
          </div>
        </div>
        <span
          className="text-xs px-2 py-1 rounded-full font-medium flex-shrink-0"
          style={{ background: "rgba(34, 197, 94, 0.15)", color: "#22C55E", border: "1px solid rgba(34, 197, 94, 0.3)" }}
        >
          Fixed
        </span>
      </div>
    );
  }

  const colors = priorityColor(issue.priority);
  return (
    <div
      className="flex items-start justify-between gap-3 rounded-xl px-4 py-3"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.dot }} />
        <div>
          <div className="text-sm font-medium" style={{ color: "#E8E4FF" }}>
            {issue.title}
          </div>
          {issue.source === "savedPriorityAction" && issue.description && (
            <div className="mt-1 text-xs leading-relaxed" style={{ color: "#9AA0B3" }}>
              {issue.description}
            </div>
          )}
        </div>
      </div>
      <span
        className="text-xs px-2 py-1 rounded-full font-medium flex-shrink-0"
        style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
      >
        {issue.priority === "HIGH" ? "HIGH" : issue.priority === "LOW" ? "LOW" : "MED"}
      </span>
    </div>
  );
};

const SavedPriorityPanel: React.FC<{ title: string; recommendations: Extract<ReportRecommendations, { status: "saved_priority" }> }> = ({
  title,
  recommendations,
}) => (
  <DataPanel title={title} icon={<AlertTriangle className="w-4 h-4" />}>
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#9AA0B3" }}>
        Saved priority
      </div>
      <p className="text-sm leading-relaxed" style={{ color: "#E8E4FF" }}>
        {recommendations.narrative}
      </p>
    </div>
  </DataPanel>
);

const ActionPlanIssuesPanel: React.FC<ActionPlanIssuesPanelProps> = ({
  cardId,
  title = "Technical Issues",
  fallback,
}) => {
  const { activeSite, selectedReportId } = useSiteContext();
  const domain = activeSite?.domain || "";
  const { getIssuesByCard, overview } = useMCPContext({ domain, reportId: selectedReportId });
  const recommendations = overview?.recommendations;
  const issues = !recommendations || recommendations.status === "structured" ? getIssuesByCard(cardId) : [];

  const exportData = issues.map((i) => ({
    Title: i.title,
    Priority: i.priority,
    Status: i.status,
    "Fixed At": i.fixedAt || "",
  }));

  if (recommendations?.status === "saved_priority") {
    return <SavedPriorityPanel title={title} recommendations={recommendations} />;
  }

  if (recommendations?.status === "unavailable") {
    return fallback ? <>{fallback}</> : (
      <DataPanel title={title} icon={<AlertTriangle className="w-4 h-4" />}>
        <EmptyState
          title="Recommendations unavailable"
          description="This report has no structured actions and no saved priority."
          icon={<AlertTriangle className="w-6 h-6" style={{ color: "#8B7BF7" }} />}
        />
      </DataPanel>
    );
  }

  if (issues.length === 0) {
    return fallback ? <>{fallback}</> : (
      <DataPanel title={title} icon={<AlertTriangle className="w-4 h-4" />}>
        <EmptyState
          title="No action items yet"
          description="Run your authority audit to see recommended fixes here."
          icon={<AlertTriangle className="w-6 h-6" style={{ color: "#8B7BF7" }} />}
        />
      </DataPanel>
    );
  }

  return (
    <DataPanel
      title={title}
      icon={<AlertTriangle className="w-4 h-4" />}
      actions={<WidgetExportBtn title={title} data={exportData} />}
    >
      <div className="space-y-2">
        {issues.map((issue) => (
          <ActionPlanIssueRow key={issue.id} issue={issue} />
        ))}
      </div>
    </DataPanel>
  );
};

export default ActionPlanIssuesPanel;

